import React, { useState, useEffect, FunctionComponent } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Redirect } from "react-router";
import NotDesktop, { Desktop } from "Shared/Responsive";
import styled from "styled-components";
import BriefcaseIcon from "src/components/icons/Briefcase";
import PencilIcon from "src/components/icons/Pencil";
import TermsIcon from "src/components/icons/Terms";
import UserIcon from "src/components/icons/User";
import Breadcrumb from "./Breadcrumb/Breadcrumb";
import { ActionSteps } from "./enums";
import Ssl from "./Ssl/Ssl";
import steps from "./StepList";
import TopBar from "./TopBar/TopBar";
import LoadingBar from "Shared/LoadingBar";
import { logError } from "src/utils/logger";

const TRANSLATION_KEY = "ContinueDeal.CreditApp";

const tabsIcons = {
    "About Me": UserIcon,
    "My Cosigner": PencilIcon,
    "My Employer": BriefcaseIcon,
    "Terms and Conditions": TermsIcon,
};

const Flex = styled("div")`
    flex: 1 1 0%;
`;

const Root = styled("div")`
    background-color: #f6f6f6;
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const Container = styled(Flex)`
    display: flex;
    flex-direction: column;
    padding: 0 56px 56px 56px;
`;

const BreadcrumbAndSslContainer = styled("div")`
    align-items: center;
    display: flex;
    font-size: 13px;
    margin-top: 30px;
`;

const ContentRoot = styled(Flex)`
    background-color: white;
    border-color: #eaeaea;
    border-radius: 0.2em;
    border-style: solid;
    border-width: 1px;
    margin-top: 10px;
`;

const LoadingContainer = styled.div`
    margin-left: 24px;
    margin-right: 24px;
    text-align: center;
    font-weight: 500;
    display: flex;
    justify-content: space-evenly;
    flex-direction: column;
    height: 55px;
    margin-top: 100px;
`;

interface ContentProps {
    currentStep: number;
    onNextClick: (data: any) => void;
    onBackClick: (data: any) => void;
    formData: any;
    formSettings: Record<string, string[]>;
}

const Content: FunctionComponent<ContentProps> = ({
    onNextClick,
    onBackClick,
    currentStep = 0,
    formData,
    formSettings,
    ...rest
}) => {
    const Step = steps[currentStep]();
    return (
        <ContentRoot>
            <Step
                {...rest}
                onNextClick={onNextClick}
                onBackClick={onBackClick}
                isCosigner={currentStep > ActionSteps.PRIMARY_END}
                formData={formData}
                formSettings={formSettings}
            />
        </ContentRoot>
    );
};

interface Props {
    breadcrumb?: {
        names: string[];
        current?: number | null;
    };
    currentStep?: number;
    done?: boolean;
    formData: any;
    tabs?: {
        names: string[];
        current?: number | null;
    };
    onNextClick: (data: any) => void;
    onBackClick: (data: any) => void;
    creditAppDealerId?: string;
}
const CreditApp: FunctionComponent<Props & WithTranslation> = ({
    currentStep = ActionSteps.PRIMARY_START,
    breadcrumb = { names: [] },
    formData,
    done = false,
    t,
    tabs = { names: [] },
    onNextClick,
    onBackClick,
    creditAppDealerId,
    ...rest
}) => {
    const [formSettings, setFormSettings] = useState({ isLoading: true, data: {} });

    useEffect(() => {
        const fetchFormData = async () => {
            try {
                const response = await fetch(
                    `https://api.prod.secure.dealerinspire.com/dealer/${creditAppDealerId}/form_data`,
                    {
                        method: "GET",
                        headers: { accept: "application/json" },
                        mode: "cors",
                    }
                );

                const settingsData = await response.json();

                if (!response.ok) {
                    console.log(`Form data fetch failure: ${settingsData.message}`);
                    // eslint-disable-next-line no-throw-literal
                    throw { name: "Non200CreditAppError", code: response.status, responseBody: settingsData };
                }

                setFormSettings(() => ({ data: settingsData.validations, isLoading: false }));
            } catch (error) {
                logError(error);

                setFormSettings((prevState) => ({ ...prevState, isLoading: false }));
            }
        };

        void fetchFormData();
    }, [creditAppDealerId]);

    if (done) {
        return <Redirect to="/continue-deal" />;
    }

    const getTabDisplayName = (tabName: string) => {
        let tabDisplayName;
        switch (tabName) {
            case "About Me":
                tabDisplayName = t(`${TRANSLATION_KEY}.aboutMeTab`);
                break;
            case "My Cosigner":
                tabDisplayName = t(`${TRANSLATION_KEY}.myCosignerTab`);
                break;
            case "My Employer":
                tabDisplayName = t(`${TRANSLATION_KEY}.myEmployerTab`);
                break;
            case "Terms and Conditions":
                tabDisplayName = t(`${TRANSLATION_KEY}.termsAndConditionsTab`);
                break;
            default:
                tabDisplayName = t(`${TRANSLATION_KEY}.aboutMeTab`);
                break;
        }

        return tabDisplayName;
    };

    const getStepDisplayName = (stepName: string) => {
        let stepDisplayName;
        switch (stepName) {
            case "Contact":
                stepDisplayName = t(`${TRANSLATION_KEY}.contactStep`);
                break;
            case "Employment":
                stepDisplayName = t(`${TRANSLATION_KEY}.employmentStep`);
                break;
            case "Identification":
                stepDisplayName = t(`${TRANSLATION_KEY}.identificationStep`);
                break;
            case "Income":
                stepDisplayName = t(`${TRANSLATION_KEY}.incomeStep`);
                break;
            case "My Cosigner":
                stepDisplayName = t(`${TRANSLATION_KEY}.myCosignerStep`);
                break;
            case "Residence":
                stepDisplayName = t(`${TRANSLATION_KEY}.residenceStep`);
                break;
            case "Terms and Conditions":
                stepDisplayName = t(`${TRANSLATION_KEY}.termsAndConditionsStep`);
                break;
            default:
                stepDisplayName = t(`${TRANSLATION_KEY}.contactStep`);
                break;
        }

        return stepDisplayName;
    };

    const { isLoading, data: formValidationsSettings } = formSettings;

    return (
        <Root>
            <TopBar
                tabs={tabs.names.map((m) => ({
                    icon: tabsIcons[m],
                    name: getTabDisplayName(m),
                }))}
                current={tabs.current}
            />

            {isLoading ? (
                <LoadingContainer>
                    <LoadingBar />
                </LoadingContainer>
            ) : (
                <>
                    <Desktop>
                        <Container>
                            <BreadcrumbAndSslContainer>
                                <Breadcrumb
                                    steps={breadcrumb.names.map((m) => getStepDisplayName(m))}
                                    current={breadcrumb.current}
                                />

                                <Flex />

                                <Ssl />
                            </BreadcrumbAndSslContainer>

                            <Content
                                {...rest}
                                onNextClick={onNextClick}
                                onBackClick={onBackClick}
                                currentStep={currentStep}
                                formData={formData}
                                formSettings={formValidationsSettings}
                            />
                        </Container>
                    </Desktop>

                    <NotDesktop>
                        <div>
                            <Content
                                {...rest}
                                onNextClick={onNextClick}
                                onBackClick={onBackClick}
                                currentStep={currentStep}
                                formData={formData}
                                formSettings={formValidationsSettings}
                            />

                            <Ssl />
                        </div>
                    </NotDesktop>
                </>
            )}
        </Root>
    );
};

export default withTranslation()(CreditApp);
